import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, AlertCircle, MinusCircle } from 'lucide-react';
import { trimInvoiceNumber } from '@/utils/invoiceUtils';

const ERP_SYNC_CONFIG = {
  NOT_SYNCED: {
    label: 'Not Synced',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: MinusCircle
  },
  SYNC_PENDING: {
    label: 'Sync Pending',
    className: 'bg-sky-50 text-sky-700 border-sky-200',
    icon: Clock
  },
  SYNCED: {
    label: 'Synced',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: CheckCircle2
  },
  SYNC_FAILED: {
    label: 'Sync Failed',
    className: 'bg-rose-50 text-rose-700 border-rose-200',
    icon: AlertCircle
  },
  NOT_APPLICABLE: {
    label: 'Clinical Only',
    className: 'bg-slate-100 text-slate-600 border-slate-200',
    icon: MinusCircle
  }
};

/**
 * Read-only ERP Synchronization Status Badge.
 * Supported states: NOT_SYNCED, SYNC_PENDING, SYNCED, SYNC_FAILED, NOT_APPLICABLE.
 * Informational only - zero user retry controls.
 */
export const ErpSyncBadge = ({ status, invoiceNo, className = '' }) => {
  const normalized = (status || 'NOT_SYNCED').toUpperCase();
  const config = ERP_SYNC_CONFIG[normalized] || ERP_SYNC_CONFIG.NOT_SYNCED;
  const Icon = config.icon;
  const displayInvoiceNo = trimInvoiceNumber(invoiceNo);

  const titleText = normalized === 'NOT_APPLICABLE'
    ? 'Clinical Only (No ERP Sync Required)'
    : displayInvoiceNo
      ? `ERP Invoice: ${displayInvoiceNo} (${config.label})`
      : `ERP Sync Status: ${config.label}`;

  return (
    <Badge
      variant="outline"
      className={`text-[11px] font-medium inline-flex items-center gap-1 py-0.5 px-2 select-none ${config.className} ${className}`}
      title={titleText}
      data-testid="erp-sync-badge"
      data-status={normalized}
    >
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span>{config.label}</span>
      {normalized === 'SYNCED' && displayInvoiceNo && (
        <span className="font-mono text-[10px] opacity-75 hidden xl:inline">({displayInvoiceNo})</span>
      )}
    </Badge>
  );
};

export default ErpSyncBadge;
